import { openDB } from 'idb';
import { ROUTES } from '../../api/routes';
import type { DBSchema } from 'idb';

interface ChamadaSyncData {
  turmaId: string;
  materiaId?: string;
  horarioAulaId?: string;
  data: string;
  alunos: { alunoId: string; status: string; observacao?: string }[];
  observacoes?: string;
}

interface OfflineDBSchema extends DBSchema {
  chamadas_outbox: {
    key: string;
    value: {
      id: string;
      payload: ChamadaSyncData;
      timestamp: number;
    };
  };
  api_get_cache: {
    key: string;
    value: {
      url: string;
      data: any;
      timestamp: number;
    };
  };
  api_write_outbox: {
    key: string;
    value: {
      id: string;
      url: string;
      method: string;
      data: any;
      headers: any;
      timestamp: number;
    };
  };
}

const DB_NAME = 'ac_offline_db';
const DB_VERSION = 2;
const STORE_CHAMADAS = 'chamadas_outbox';
const STORE_GET_CACHE = 'api_get_cache';
const STORE_WRITE_OUTBOX = 'api_write_outbox';

async function getDB() {
  return openDB<OfflineDBSchema>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        db.createObjectStore(STORE_CHAMADAS, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORE_GET_CACHE)) {
        db.createObjectStore(STORE_GET_CACHE, { keyPath: 'url' });
      }
      if (!db.objectStoreNames.contains(STORE_WRITE_OUTBOX)) {
        db.createObjectStore(STORE_WRITE_OUTBOX, { keyPath: 'id' });
      }
    },
  });
}

/**
 * Adiciona uma chamada à fila de sincronização offline.
 */
export async function addChamadaToSyncQueue(payload: ChamadaSyncData) {
  const db = await getDB();
  const id = `chamada_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  await db.put(STORE_CHAMADAS, { id, payload, timestamp: Date.now() });
  return id;
}

/**
 * Conta quantas chamadas estão na fila pendentes de sincronização.
 */
export async function getSyncQueueCount(): Promise<number> {
  try {
    const db = await getDB();
    return await db.count(STORE_CHAMADAS);
  } catch (e) {
    return 0;
  }
}

/**
 * Tenta processar todas as chamadas na fila.
 * Se a requisição for bem-sucedida, remove da fila.
 */
export async function flushSyncQueue(escolaId: string) {
  if (!navigator.onLine) return;

  const db = await getDB();
  const pending = await db.getAll(STORE_CHAMADAS);

  if (pending.length === 0) return;

  console.log(`[OfflineSync] Flushing ${pending.length} pending items...`);

  // Importação dinâmica para evitar dependência circular estática com client.ts
  const { api } = await import('../../api/client');

  for (const item of pending) {
    try {
      await api.post(ROUTES.chamadas ? ROUTES.chamadas.listar(escolaId) : `/escolas/${escolaId}/chamadas`, item.payload);
      // Sucesso na API, podemos remover da fila local
      await db.delete(STORE_CHAMADAS, item.id);
      console.log(`[OfflineSync] Chamada ${item.id} synced successfully!`);
    } catch (err: any) {
      const status = err.response?.status;
      if (status && status >= 400 && status < 500) {
        console.error(`[OfflineSync] Chamada ${item.id} rejected by server with ${status}. Removing from queue.`, err);
        await db.delete(STORE_CHAMADAS, item.id);
      } else {
        console.warn(`[OfflineSync] Chamada ${item.id} failed to sync (network error?). Keeping in queue.`);
      }
    }
  }
}

/**
 * Busca a resposta salva para um GET request no cache local.
 */
export async function getCachedGetResponse(url: string): Promise<any | null> {
  try {
    const db = await getDB();
    const cached = await db.get(STORE_GET_CACHE, url);
    return cached ? cached.data : null;
  } catch (e) {
    console.error('[OfflineCache] Erro ao ler cache GET:', e);
    return null;
  }
}

/**
 * Salva a resposta de sucesso de um GET request no cache local.
 */
export async function cacheGetResponse(url: string, data: any): Promise<void> {
  try {
    const db = await getDB();
    await db.put(STORE_GET_CACHE, { url, data, timestamp: Date.now() });
  } catch (e) {
    console.error('[OfflineCache] Erro ao gravar cache GET:', e);
  }
}

/**
 * Enfileira um write request (POST, PUT, PATCH, DELETE) que falhou no outbox genérico.
 */
export async function queueWriteRequest(config: { url: string; method: string; data: any; headers: any }): Promise<string> {
  const db = await getDB();
  const id = `write_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  // Limpar headers desnecessários e manter apenas os relevantes para serialização segura
  const serializedHeaders = { ...config.headers };
  delete serializedHeaders['Content-Type'];
  delete serializedHeaders['Authorization'];
  delete serializedHeaders['X-CSRF-Token'];
  delete serializedHeaders['X-AC-Fingerprint'];
  delete serializedHeaders['X-AC-Device-UUID'];

  await db.put(STORE_WRITE_OUTBOX, {
    id,
    url: config.url,
    method: config.method,
    data: config.data,
    headers: serializedHeaders,
    timestamp: Date.now(),
  });
  return id;
}

/**
 * Processa a fila de outbox genérico (POST, PUT, PATCH, DELETE).
 */
export async function flushGenericOutbox(): Promise<void> {
  if (!navigator.onLine) return;

  try {
    const db = await getDB();
    const pending = await db.getAll(STORE_WRITE_OUTBOX);

    if (pending.length === 0) return;

    // Ordenar cronologicamente
    pending.sort((a, b) => a.timestamp - b.timestamp);

    console.log(`[OfflineOutbox] Flushing ${pending.length} generic outbox items...`);

    // Importação dinâmica para evitar dependência circular estática com client.ts
    const { api } = await import('../../api/client');

    for (const item of pending) {
      try {
        // Executar a requisição com bypass header para não re-enfileirar caso falhe
        await api.request({
          url: item.url,
          method: item.method,
          data: item.data,
          headers: {
            ...item.headers,
            'X-AC-Bypass-Outbox': 'true',
          },
        });

        // Sucesso: deleta da fila local
        await db.delete(STORE_WRITE_OUTBOX, item.id);
        console.log(`[OfflineOutbox] Item ${item.id} (${item.method} ${item.url}) synced successfully!`);
      } catch (err: any) {
        const status = err.response?.status;
        // Se for erro de validação do cliente (4xx), descarta para evitar bloquear a fila
        if (status && status >= 400 && status < 500) {
          console.error(`[OfflineOutbox] Item ${item.id} rejected with ${status}. Removing from queue.`, err);
          await db.delete(STORE_WRITE_OUTBOX, item.id);
        } else {
          // Erro de rede ou 5xx: mantém na fila e pausa o processamento para tentar mais tarde
          console.warn(`[OfflineOutbox] Item ${item.id} failed to sync (network/server error). Pausing outbox flush.`);
          break;
        }
      }
    }
  } catch (e) {
    console.error('[OfflineOutbox] Falha ao processar outbox:', e);
  }
}

/**
 * Limpa completamente todas as bases locais offline no IndexedDB (GET cache, outbox genérico e chamadas).
 * Invocado no logout/troca de conta para evitar vazamento de dados privados entre usuários.
 */
export async function clearOfflineCache(): Promise<void> {
  try {
    const db = await getDB();
    await db.clear(STORE_GET_CACHE);
    await db.clear(STORE_WRITE_OUTBOX);
    await db.clear(STORE_CHAMADAS);
    console.log('[OfflineCache] Todas as stores do IndexedDB local foram limpas.');
  } catch (e) {
    console.error('[OfflineCache] Erro ao limpar IndexedDB local:', e);
  }
}

